import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import '@/styles/tactical-ui.css';
import usePageTitle from "@/hooks/usePageTitle";

const Login = () => {
  usePageTitle("pageTitles.login");
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [devBypass, setDevBypass] = useState(false);

  // Post-login routing: admins to /admin-kv, everyone else to /recipes.
  // Receives the freshly-minted session DIRECTLY from supabase.auth.signInWithPassword
  // so we don't race against the auth-state-change listener that may not have
  // populated localStorage yet.  Uses supabase.from() so RLS is enforced
  // with the real JWT.
  const routeByRole = async (signInData) => {
    try {
      const userId = signInData?.user?.id || signInData?.session?.user?.id;
      if (!userId) {
        console.warn("routeByRole: no user_id from signIn response", signInData);
        navigate("/recipes");
        return;
      }
      const { data: profile, error: profErr } = await supabase
        .from("profiles")
        .select("is_super_admin, email")
        .eq("user_id", userId)
        .maybeSingle();
      if (profErr) {
        console.warn("routeByRole: profile lookup failed", profErr);
      }
      console.info("routeByRole:", {
        userId,
        email: signInData?.user?.email,
        is_super_admin: profile?.is_super_admin,
      });
      navigate(profile?.is_super_admin ? "/admin-kv" : "/recipes");
    } catch (err) {
      console.error("routeByRole error:", err);
      navigate("/recipes");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Dev bypass and normal path both call the same signIn — Supabase
      // returns the fresh session inline so we don't need a separate flow.
      const signInData = await signIn(email, password);
      await routeByRole(signInData);
    } catch (err) {
      console.error('Login error:', err);
      
      // More helpful error messages
      if (err.message?.includes('Email not confirmed')) {
        if (email.toLowerCase() === 'kritajnaventures@gmail.com' || 
            email.toLowerCase() === 'superkrita@plateprofit.com') {
          setError('Enable "Dev Bypass" toggle below to skip email verification (test accounts only)');
        } else {
          setError('Please verify your email before logging in. Check your inbox for the verification link.');
        }
      } else if (err.message?.includes('Invalid')) {
        setError('Invalid email or password. Please check your credentials.');
      } else {
        setError(err.message || 'Failed to log in. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-amber-500 mb-2">PlateProfit</h1>
          <p className="text-slate-400 text-lg">Welcome back, Chef!</p>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-slate-300 mb-2 font-medium">Email</label>
            <input
              type="email"
              className="auth-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="chef@restaurant.com"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-slate-300 font-medium">Password</label>
              <Link 
                to="/forgot-password" 
                className="text-sm text-amber-500 hover:text-amber-400 font-medium transition-colors"
              >
                Forgot Password?
              </Link>
            </div>
            <input
              type="password"
              className="auth-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="••••••••"
            />
          </div>
          
          {/* Dev Bypass Toggle - Test Accounts Only */}
          {(email.toLowerCase() === 'kritajnaventures@gmail.com' || 
            email.toLowerCase() === 'superkrita@plateprofit.com' ||
            email.toLowerCase().includes('superkrita')) && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="devBypass"
                    checked={devBypass}
                    onChange={(e) => setDevBypass(e.target.checked)}
                    className="w-4 h-4 text-amber-500 bg-slate-800 border-slate-700 rounded focus:ring-amber-500"
                  />
                  <label htmlFor="devBypass" className="text-sm font-medium text-amber-400">
                    🔧 Dev Bypass (Skip Email Verification)
                  </label>
                </div>
              </div>
              <p className="text-xs text-amber-500/70 mt-2">
                Test account detected. Enable to skip SMTP verification during dev sprint.
              </p>
            </div>
          )}

          <button
            type="submit"
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-slate-400">
            Don't have an account?{' '}
            <Link to="/signup" className="text-amber-500 hover:text-amber-400 font-semibold">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
